import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import dayjs from 'dayjs';
import { parseDate, businessToday } from '../utils/date-utils';
import PageLayout from '../components/ui/PageLayout';
import { IconButton, buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { IconClose } from '../components/ui/icons';
import { Field, TextArea } from '../components/ui/Field';
import SearchField from '../components/ui/SearchField';
import Checkbox from '../components/ui/Checkbox';
import Tag from '../components/ui/Tag';
import LogoSpinner from '../components/ui/LogoSpinner';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';

/* ── Shared UI helpers (match EventDetail pattern) ── */

/* ── Timeline visualization for a single tour card ── */

const TourTimeline = ({ tour }) => {
  const events = (tour.events || [])
    .slice()
    .sort((a, b) => dayjs(a.event_date).diff(dayjs(b.event_date)));

  if (events.length === 0) return null;

  // Build points: depart, each show, return
  const points = [];

  if (tour.window_start) {
    points.push({
      date: tour.window_start,
      label: 'Depart',
      type: 'transit',
    });
  }

  events.forEach((ev) => {
    points.push({
      date: ev.event_date,
      label: ev.name,
      type: 'show',
    });
  });

  if (tour.window_end) {
    points.push({
      date: tour.window_end,
      label: 'Return',
      type: 'transit',
    });
  }

  if (points.length < 2) return null;

  return (
    <div className="mt-3 px-2 overflow-x-auto">
      <div className="relative flex items-start min-w-0" style={{ minWidth: points.length * 90 }}>
        {/* Connecting line */}
        <div
          className="absolute top-[7px] bg-gray-750 h-[2px]"
          style={{
            left: 16,
            right: 16,
          }}
        />

        {points.map((pt, i) => (
          <div
            key={`${pt.date}-${i}`}
            className="flex flex-col items-center relative z-10"
            style={{ flex: 1, minWidth: 70 }}
          >
            {/* Circle */}
            <div
              className={`w-3.5 h-3.5 rounded-full border-2 ${
                pt.type === 'show'
                  ? 'bg-primary border-primary'
                  : 'bg-surface-container-highest border-outline'
              }`}
            />
            {/* Date */}
            <span className="text-[10px] text-on-surface-variant mt-1.5 whitespace-nowrap">
              {dayjs(pt.date).format('MMM D')}
            </span>
            {/* Label */}
            <span
              className={`text-[10px] mt-0.5 whitespace-nowrap truncate max-w-[80px] text-center ${
                pt.type === 'show' ? 'text-on-surface font-medium' : 'text-on-surface-variant'
              }`}
            >
              {pt.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Create Tour Modal ── */

const CreateTourModal = ({ events, onClose, onCreated }) => {
  const api = useApi();
  const toast = useToast();
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState('');
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [eventSearch, setEventSearch] = useState('');

  // Available: no tour, not cancelled/completed, sorted by date
  const available = useMemo(
    () =>
      (events || [])
        .filter(
          (e) =>
            !e.tour_id &&
            e.status !== 'cancelled' &&
            e.status !== 'completed'
        )
        .sort((a, b) => dayjs(a.event_date).diff(dayjs(b.event_date))),
    [events]
  );

  // Filtered by search
  const filteredAvailable = useMemo(() => {
    if (!eventSearch.trim()) return available;
    const q = eventSearch.toLowerCase();
    return available.filter(e =>
      e.name?.toLowerCase().includes(q) ||
      e.location?.toLowerCase().includes(q)
    );
  }, [available, eventSearch]);

  // Auto-generate name from earliest selected event (only if user hasn't manually edited)
  useEffect(() => {
    if (nameManuallyEdited) return;
    if (selected.length === 0) {
      setName('');
      return;
    }
    const earliest = available
      .filter((e) => selected.includes(e.id))
      .sort((a, b) => dayjs(a.event_date).diff(dayjs(b.event_date)))[0];
    if (earliest) {
      const d = dayjs(earliest.event_date);
      setName(`Tour ${d.format('MDD')}`);
    }
  }, [selected, available, nameManuallyEdited]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleCreate = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one event');
      return;
    }
    setCreating(true);
    try {
      const tour = await api.post('/tours', {
        body: { name, notes, event_ids: selected },
      });
      toast.success('Tour created');
      onCreated(tour);
    } catch (err) {
      toast.error('Failed to create tour');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="New Tour"
      size="lg"
      dismissible={false}
      dirty={selected.length > 0 || notes !== '' || (nameManuallyEdited && name !== '')}
      busy={creating}
      footer={(
        <>
          <button
            onClick={onClose}
            className={buttonClass('quiet')}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || selected.length === 0}
            className={buttonClass('filled')}
          >
            {creating ? 'Creating...' : 'Create Tour'}
          </button>
        </>
      )}
    >
        {/* Body */}
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1.5">
              Tour Name
            </label>
            <Field
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameManuallyEdited(true); }}
              placeholder="Auto-generated from first event date"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1.5">
              Notes
            </label>
            <TextArea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes..."
            />
          </div>

          {/* Event picker */}
          <div>
            <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1.5">
              Select Events ({selected.length} selected)
            </label>
            <SearchField
              label="Search events"
              value={eventSearch}
              onChange={setEventSearch}
              placeholder="Search events..."
              className="mb-2"
            />
            {/* Pinned selected events */}
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selected.map(sid => {
                  const ev = available.find(e => e.id === sid);
                  if (!ev) return null;
                  return (
                    <button
                      key={sid}
                      type="button"
                      onClick={() => toggle(sid)}
                      className="inline-flex items-center gap-1.5 text-xs text-primary-ink bg-primary/10 px-2 py-1 rounded-full ring-1 ring-inset ring-primary/30 hover:bg-primary/20 transition-colors"
                    >
                      {ev.name}
                      <span className="text-primary/50">{dayjs(ev.event_date).format('M/D')}</span>
                      <svg className="w-3 h-3 text-primary/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  );
                })}
              </div>
            )}
            {available.length === 0 ? (
              <EmptyState variant="area">
                No available events to add
              </EmptyState>
            ) : (
              <div className="space-y-1 max-h-52 overflow-y-auto rounded-md border border-gray-700/40 bg-gray-900/50 p-1">
                {filteredAvailable.filter(ev => !selected.includes(ev.id)).map((ev) => (
                  <Checkbox
                    key={ev.id}
                    fill
                    checked={false}
                    onChange={() => toggle(ev.id)}
                    label={<span className="block truncate">{ev.name}</span>}
                    description={<>
                      {dayjs(ev.event_date).format('MMM D, YYYY')}
                      {ev.location && ` \u2022 ${ev.location}`}
                    </>}
                    className="px-3 py-2 rounded-md transition-colors hover:bg-surface-container"
                  />
                ))}
                {filteredAvailable.filter(ev => !selected.includes(ev.id)).length === 0 && eventSearch.trim() && (
                  <p className="text-on-surface-variant text-xs py-3 text-center">No matches for "{eventSearch}"</p>
                )}
              </div>
            )}
          </div>
        </div>
    </Dialog>
  );
};

/* ── One tour card ── */

const TourCard = ({ tour }) => {
  const events = (tour.events || [])
    .slice()
    .sort((a, b) => dayjs(a.event_date).diff(dayjs(b.event_date)));
  const firstDate = events[0]?.event_date;
  const lastDate = events[events.length - 1]?.event_date;
  const dateRange =
    firstDate && lastDate && firstDate !== lastDate
      ? `${dayjs(firstDate).format('MMM D')} \u2013 ${dayjs(lastDate).format('MMM D')}`
      : firstDate
      ? dayjs(firstDate).format('MMM D, YYYY')
      : '\u2014';

  const totalDrones = tour.committed_total || 0;
  const pyroDrones = tour.committed_pyro || 0;

  return (
    <Panel label={tour.name} padding="md">
      {/* Card header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Link
            to={`/events/tours/${tour.id}`}
            className="text-on-surface font-semibold text-lg hover:text-primary transition-colors"
          >
            {tour.name}
          </Link>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm">
            <span className="text-on-surface-variant">{dateRange}</span>
            <span className="text-on-surface-variant">
              {events.length} show{events.length !== 1 ? 's' : ''}
            </span>
            {totalDrones > 0 && (
              <span className="text-on-surface-variant">
                {totalDrones} drones
                {pyroDrones > 0 && (
                  <span className="text-on-surface-variant">
                    {' '}({pyroDrones} pyro)
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <Link
          to={`/events/tours/${tour.id}`}
          className="text-on-surface-variant hover:text-on-surface transition-colors shrink-0 mt-1"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      {/* Timeline */}
      <TourTimeline tour={tour} />

      {/* Event names below timeline */}
      {events.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-700/40">
          {events.map((ev) => (
            <Tag key={ev.id}>{ev.name}</Tag>
          ))}
        </div>
      )}
    </Panel>
  );
};

/* ── Main TourList component ── */

const TourList = () => {
  const api = useApi();
  const toast = useToast();
  const history = useHistory();
  const [tours, setTours] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [toursData, eventsData] = await Promise.all([
        api.get('/tours'),
        api.get('/events'),
      ]);
      setTours(toursData || []);
      setAllEvents(eventsData || []);
    } catch (err) {
      toast.error('Failed to load tours');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  // Tours out today or still ahead come first, soonest first, then tours with
  // no events. Finished tours sit behind a toggle, most recent first.
  const { currentTours, pastTours } = useMemo(() => {
    const today = businessToday().format('YYYY-MM-DD');
    const day = (d) => (d ? parseDate(d).format('YYYY-MM-DD') : null);
    const startOf = (t) => day(t.window_start || t.events?.[0]?.event_date);
    const endOf = (t) => day(t.window_end || t.events?.[t.events.length - 1]?.event_date);

    const current = [];
    const past = [];
    const undated = [];
    tours.forEach((t) => {
      const end = endOf(t);
      if (!startOf(t) || !end) undated.push(t);
      else if (end >= today) current.push(t);
      else past.push(t);
    });
    current.sort((a, b) => startOf(a).localeCompare(startOf(b)));
    past.sort((a, b) => endOf(b).localeCompare(endOf(a)));

    return { currentTours: [...current, ...undated], pastTours: past };
  }, [tours]);

  const handleCreated = (tour) => {
    setShowCreate(false);
    if (tour?.id) {
      history.push(`/events/tours/${tour.id}`);
    }
  };

  if (loading) return <LogoSpinner label="Loading tours" />;

  return (
    <PageLayout kind="workspace" className="pt-4 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <BackLink to="/events" label="Events" className="mb-2" />
          <h1 className="text-2xl font-bold text-primary">Tours</h1>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className={buttonClass('filled')}
        >
          + New Tour
        </button>
      </div>

      {/* Tour cards */}
      {tours.length === 0 ? (
        <EmptyState title="No tours yet">
          Group events into tours to optimize fleet tracking.
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {currentTours.map((tour) => (
            <TourCard key={tour.id} tour={tour} />
          ))}
          {currentTours.length === 0 && (
            <EmptyState variant="area">
              Nothing out or coming up. Past tours are below.
            </EmptyState>
          )}
          {pastTours.length > 0 && (
            <button
              type="button"
              onClick={() => setShowPast((v) => !v)}
              aria-expanded={showPast}
              className="text-sm text-on-surface-variant hover:text-on-surface transition-colors"
            >
              {showPast ? 'Hide past tours' : `Show past tours (${pastTours.length})`}
            </button>
          )}
          {showPast && pastTours.map((tour) => (
            <TourCard key={tour.id} tour={tour} />
          ))}
        </div>
      )}

      {/* Create Tour Modal */}
      {showCreate && (
        <CreateTourModal
          events={allEvents}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </PageLayout>
  );
};

export default TourList;
